import { ReactElement } from "react";
import styled, { css } from "styled-components";

type TextPosition = "top" | "center" | "bottom";
type TextAlign = "left" | "center";

interface HeroData {
  title?: string;
  subtitle?: string;
  image?: { url?: string };
  overlay?: boolean;
  /** Where the overlaid text sits on the image. Default: bottom. */
  textPosition?: TextPosition;
  /** Horizontal alignment of the overlaid/below text. Default: left. */
  textAlign?: TextAlign;
  /** 'full' = edge-to-edge cover; 'inset' (default) = rounded card in the padding. */
  width?: "inset" | "full";
}

const Wrap = styled.div<{ $fullBleed: boolean }>`
  position: relative;
  border-radius: 16px;
  overflow: hidden;

  /* Full-bleed cover: escape the hosting container's padding via the
     --content-inset(-top) contract (DisplayContent, SwiperOnboarding declare
     theirs). Undeclared hosts resolve to 0px — the hero just stays in flow. */
  ${({ $fullBleed }) =>
    $fullBleed &&
    css`
      border-radius: 0;
      margin-inline: calc(var(--content-inset, 0px) * -1);
    `}
`;

/* When a full-bleed hero opens the page, it also swallows the top padding so the
   cover touches the header — but only as the FIRST block (mid-page heroes keep
   the flow rhythm). */
const HeroRoot = styled.div<{ $fullBleed: boolean }>`
  ${({ $fullBleed }) =>
    $fullBleed &&
    css`
      &:first-child > ${Wrap} {
        margin-top: calc(var(--content-inset-top, 0px) * -1);
      }
    `}
`;

const Img = styled.img`
  width: 100%;
  display: block;
  aspect-ratio: 16 / 10;
  object-fit: cover;
`;

// Full-height contrast ramp (as a MASK: alpha = how much of the darkened copy shows),
// darkest where the text sits. Tuned by hand (user-picked): the ramp never reaches
// zero — a gentle ~31% veil at the edges rising to ~76% at the text — so the whole
// image carries a soft, continuous gradient instead of a localized dark band.
const SCRIM_MASKS: Record<TextPosition, string> = {
  top: "linear-gradient(#000000c2, #0000004f)",
  center: "linear-gradient(#0000004f, #000000c2, #0000004f)",
  bottom: "linear-gradient(#0000004f, #000000c2)",
};

/**
 * Full-height scrim that FOLLOWS THE IMAGE'S ALPHA: a darkened COPY of the image
 * (filter: brightness — transparent pixels stay transparent) is overlaid and masked
 * by the contrast GRADIENT. On photos this reads as the classic cinematic scrim; on
 * transparent PNGs the darkening can only land on actual pixels — see-through
 * regions stay clean instead of compositing gray over the page.
 *
 * Why this shape: masking a dark veil BY the image (mask-image: url(...)) would be
 * the direct construction, but CSS masks are CORS-gated and the bucket images are
 * cross-origin — a failed mask fetch resolves to transparent black and the scrim
 * vanishes entirely. Gradients are generated images: no fetch, no CORS.
 * Same URL as the base <img> → served from cache, downloaded once.
 */
const DarkImageCopy = styled.img<{ $position: TextPosition }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  filter: brightness(0.68);
  -webkit-mask-image: ${({ $position }) => SCRIM_MASKS[$position]};
  mask-image: ${({ $position }) => SCRIM_MASKS[$position]};
`;

const JUSTIFY: Record<TextPosition, string> = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
};

const TextLayer = styled.div<{ $position: TextPosition; $align: TextAlign }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: ${({ $position }) => JUSTIFY[$position]};
  align-items: ${({ $align }) => ($align === "center" ? "center" : "flex-start")};
  text-align: ${({ $align }) => $align};
  padding: 18px 16px 14px;
`;

const OverlayTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  text-wrap: balance;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
`;

const OverlaySubtitle = styled.p`
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.92);
  font-size: 15px;
  line-height: 1.4;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
`;

const BelowTitle = styled.h2<{ $align: TextAlign }>`
  margin: 12px 0 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--app-color-text, #041723);
  text-wrap: balance;
  text-align: ${({ $align }) => $align};
`;

const BelowSubtitle = styled.p<{ $align: TextAlign }>`
  margin: 4px 0 0;
  font-size: 15px;
  line-height: 1.5;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.7);
  text-align: ${({ $align }) => $align};
`;

const HeroBlock = ({ data }: { data: HeroData }): ReactElement | null => {
  const url = data.image?.url;
  const overlay = data.overlay !== false;
  const hasText = !!(data.title || data.subtitle);
  if (!url && !hasText) return null;

  const position: TextPosition =
    data.textPosition === "top" || data.textPosition === "center" ? data.textPosition : "bottom";
  const align: TextAlign = data.textAlign === "center" ? "center" : "left";
  const fullBleed = data.width === "full";

  return (
    <HeroRoot $fullBleed={fullBleed}>
      {url && (
        <Wrap $fullBleed={fullBleed}>
          <Img src={url} alt={overlay ? "" : data.title ?? ""} />
          {overlay && hasText && (
            <>
              <DarkImageCopy src={url} alt="" aria-hidden="true" $position={position} />
              <TextLayer $position={position} $align={align}>
                {data.title ? <OverlayTitle>{data.title}</OverlayTitle> : null}
                {data.subtitle ? <OverlaySubtitle>{data.subtitle}</OverlaySubtitle> : null}
              </TextLayer>
            </>
          )}
        </Wrap>
      )}
      {(!overlay || !url) && data.title ? <BelowTitle $align={align}>{data.title}</BelowTitle> : null}
      {(!overlay || !url) && data.subtitle ? (
        <BelowSubtitle $align={align}>{data.subtitle}</BelowSubtitle>
      ) : null}
    </HeroRoot>
  );
};

export default HeroBlock;
